import { createFileRoute } from "@tanstack/react-router"
import { Users } from "lucide-react"

import { initials } from "@anysola/contracts/shared/initials"
import {
  TEAM_SOCIAL_LABELS,
  type PublicTeamMember,
} from "@anysola/contracts/web/team-core"
import { Button } from "@anysola/ui/button"
import { EmptyState } from "@anysola/ui/empty-state"
import { RowGroup, rowBase } from "@anysola/ui/row-group"
import { cn } from "@anysola/ui/utils"

import { UnavailableAlert } from "@/components/feedback/unavailable-alert"
import { Container } from "@/components/layout/container"
import { SiteShell } from "@/components/layout/site-shell"
import { pageHead } from "@/lib/metadata"
import { getTeam } from "@/server/web"

/** A square photo, or the person's initials in the same box. */
function Photo({ member }: { member: PublicTeamMember }) {
  if (member.photo) {
    return (
      <img
        src={member.photo}
        alt=""
        width={56}
        height={56}
        loading="lazy"
        decoding="async"
        className="size-14 shrink-0 rounded-lg bg-default object-cover"
      />
    )
  }
  return (
    <span
      aria-hidden
      className="grid size-14 shrink-0 place-items-center rounded-lg bg-default text-sm text-muted-foreground"
    >
      {initials(member.name)}
    </span>
  )
}

/** Opens in a new tab: every link here leaves the site. */
function OutLink({
  href,
  label,
  children,
}: {
  href: string
  label: string
  children: React.ReactNode
}) {
  return (
    <Button
      variant="ghost"
      render={
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
        />
      }
    >
      {children}
    </Button>
  )
}

function MemberRow({ member }: { member: PublicTeamMember }) {
  return (
    <li className={cn(rowBase, "gap-4 max-sm:flex-wrap")}>
      <Photo member={member} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-foreground">
          {member.name}
        </span>
        <span className="mt-0.5 block truncate text-sm text-muted-foreground">
          {member.role}
        </span>
        {member.note ? (
          <span className="mt-0.5 block truncate text-sm text-muted-foreground">
            {member.note}
          </span>
        ) : null}
      </span>
      <span className="flex shrink-0 flex-wrap items-center gap-1 max-sm:w-full max-sm:pl-14">
        <OutLink
          href={member.portfolio}
          label={`${member.name}’s portfolio (opens in a new tab)`}
        >
          Portfolio
          <span aria-hidden>↗</span>
        </OutLink>
        {member.socials.map((social) => (
          <OutLink
            key={`${social.kind}-${social.url}`}
            href={social.url}
            label={`${member.name} on ${TEAM_SOCIAL_LABELS[social.kind]} (opens in a new tab)`}
          >
            {TEAM_SOCIAL_LABELS[social.kind]}
          </OutLink>
        ))}
      </span>
    </li>
  )
}

/** Everyone published in Admin's People, in its order. */
function TeamPage() {
  const team = Route.useLoaderData()

  return (
    <SiteShell>
      <h1 className="sr-only">Team</h1>
      <Container className="pt-12 pb-16 sm:pt-16 sm:pb-24">
        {team === null ? (
          <UnavailableAlert title="The team page is unavailable right now." />
        ) : team.length === 0 ? (
          <EmptyState
            icon={<Users />}
            title="No one listed yet"
            className="w-full py-16"
          />
        ) : (
          <RowGroup label="Team">
            {team.map((member) => (
              <MemberRow key={member.id} member={member} />
            ))}
          </RowGroup>
        )}
      </Container>
    </SiteShell>
  )
}

export const Route = createFileRoute("/team")({
  // Admin edits revalidate this page; the TTL (300 s, in the service) only
  // bounds a direct database edit.
  loader: () => getTeam(),
  head: () =>
    pageHead({
      title: "Team",
      description: "The people behind Anysola and their work.",
      path: "/team",
      image: "/team/opengraph-image",
    }),
  component: TeamPage,
})
